import React from 'react';
import { Users, Award, Shield, Heart, Headphones, ThumbsUp, MapPin, Calendar, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const WhyChooseUs = () => {
  const stats = [
    { icon: Users, value: '10,000+', label: 'Happy Clients' },
    { icon: Award, value: '50+', label: 'Destinations' },
    { icon: Calendar, value: '5+', label: 'Years Experience' },
    { icon: ThumbsUp, value: '100%', label: 'Satisfaction Rate' }
  ];

  const reasons = [
    {
      icon: Users,
      title: 'Trusted by 10,000+ Travelers',
      description: 'We have successfully served over 10,000 satisfied clients, creating unforgettable memories and experiences across India and the world.',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: Award,
      title: 'Expert Travel Planning',
      description: 'Our experienced team crafts personalized itineraries that match your preferences, budget, and travel style perfectly.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Shield,
      title: 'Safe & Secure Travel',
      description: 'Your safety is our priority. We ensure all our packages include proper insurance, verified accommodations, and reliable transportation.',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Heart,
      title: 'Best Price Guarantee',
      description: 'We offer competitive pricing without compromising on quality. Get the best value for your money with transparent pricing.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Headphones,
      title: '24/7 Customer Support',
      description: 'Our dedicated support team is available round the clock to assist you before, during, and after your journey.',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: Sparkles,
      title: 'Customized Experiences',
      description: 'We understand every traveler is unique. We customize packages to create experiences that match your dreams.',
      color: 'from-pink-500 to-pink-600'
    }
  ];

  const locations = [
    {
      city: 'Panchkula',
      address: 'Sector 8, Panchkula',
      state: 'Haryana',
      image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600'
    },
    {
      city: 'Parwanoo',
      address: 'Sector 1, SCF 14, Parwanoo',
      state: 'Himachal Pradesh',
      image: 'https://images.unsplash.com/photo-1506461883276-594a12b11cf3?w=600'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      location: 'Delhi',
      comment: 'TUM Holidays made our Ladakh trip absolutely amazing! Professional service and great value for money.',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      location: 'Mumbai',
      comment: 'Best travel agency I have worked with. The Bali package was perfect in every way!',
      rating: 5
    },
    {
      name: 'Amit Verma',
      location: 'Bangalore',
      comment: 'Highly recommend TUM Holidays! Their team is responsive and takes care of every detail.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative h-96 mb-20 overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200" 
          alt="Why Choose Us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center text-center text-white">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Why Choose TUM Holidays?
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Your Journey, To Ur Manzil - Trusted by over 10,000 happy travelers
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-4 mb-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon size={32} className="text-red-600" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Main Reasons Section */}
      <section className="container mx-auto px-4 mb-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Makes Us Different
          </h2>
          <p className="text-xl text-gray-600">
            Discover why thousands choose TUM Holidays for their travel adventures
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${reason.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <Icon size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{reason.title}</h3>
                <p className="text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Our Locations */}
      <section className="bg-gradient-to-br from-red-50 to-blue-50 py-20 mb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Visit Our Offices
            </h2>
            <p className="text-xl text-gray-600">
              We're here to help you plan your perfect journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {locations.map((location, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <div className="relative h-48">
                  <img 
                    src={location.image} 
                    alt={location.city}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-2xl font-bold text-white">{location.city}</h3>
                    <p className="text-white/90">{location.state}</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start gap-3">
                    <MapPin size={24} className="text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-gray-900 font-semibold text-lg">{location.address}</p>
                      <p className="text-gray-600 mt-2">{location.state}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="container mx-auto px-4 mb-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600">
            Real feedback from our 10,000+ satisfied clients
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">⭐</span>
                ))}
              </div>
              <p className="text-gray-700 italic mb-6">"{testimonial.comment}"</p>
              <div>
                <p className="font-bold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-gray-600">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4">
        <div className="bg-gradient-to-r from-red-600 to-blue-600 rounded-3xl p-8 md:p-16 text-white text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Join 10,000+ Happy Travelers
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you create memories that last a lifetime. Start your journey with TUM Holidays today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Link to="/contact">Book Your Trip Now</Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-red-600 text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
            >
              <Link to="/domestic">Explore Packages</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;